import { useEffect, useState } from 'react';
import { assignTriageToCase } from 'services/escriturasTriageService';
import { searchClientsBasic } from 'services/contractGeneratorService';
import { getEscriturasCasesForClient } from 'services/escriturasIntakeService';

/**
 * Asignación cliente-céntrica: el usuario busca al CLIENTE (nombre/RUT) con el
 * ComboBox del design system (onInputChange + búsqueda dinámica, mismo patrón de
 * ManageTaskModal). Elegido el cliente, se cargan sus casos EEPP (businessAreaId=11)
 * y se auto-selecciona si tiene uno solo. El backend re-inyecta el correo al pipeline.
 */
export default function TriageAssignCaseModal({ DesignSystemComponents, detail, t, onClose, onResolved }) {
  const { Modal, Button, ComboBox } = DesignSystemComponents ?? {};

  const [clientSearchText, setClientSearchText] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null); // option {label, value, clientId}

  const [eppCases, setEppCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Búsqueda dinámica de clientes (debounce 400ms, mín. 3 caracteres).
  useEffect(() => {
    const deb = setTimeout(async () => {
      if (!clientSearchText || clientSearchText.length < 3) {
        setClientOptions([]);
        return;
      }
      setLoadingClients(true);
      try {
        const result = await searchClientsBasic({ text: clientSearchText });
        setClientOptions(
          (result || []).map((c) => ({
            label: c.rut ? `${c.name} (${c.rut})` : c.name,
            value: c.id,
            clientId: c.clientId ?? c.id,
          }))
        );
      } catch {
        setClientOptions([]);
      } finally {
        setLoadingClients(false);
      }
    }, 400);
    return () => clearTimeout(deb);
  }, [clientSearchText]);

  // Casos EEPP del cliente elegido.
  useEffect(() => {
    if (!selectedClient) {
      setEppCases([]);
      setSelectedCaseId(null);
      return undefined;
    }
    let cancelled = false;
    setCasesLoading(true);
    setEppCases([]);
    setSelectedCaseId(null);
    (async () => {
      try {
        const cases = await getEscriturasCasesForClient(selectedClient.clientId);
        if (cancelled) return;
        setEppCases(cases || []);
        if ((cases || []).length === 1) setSelectedCaseId(cases[0].caseId);
      } catch {
        if (!cancelled) setEppCases([]);
      } finally {
        if (!cancelled) setCasesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClient]);

  const handleConfirm = async () => {
    if (!selectedCaseId) return;
    setSending(true);
    setError(null);
    try {
      const result = await assignTriageToCase(detail.id, { caseId: selectedCaseId, notes: notes || null });
      onResolved(result?.message);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('triage_error_generic'));
      setSending(false);
    }
  };

  const body = (
    <div>
      <p className='font-light mb-3' style={{ fontSize: 13 }}>{t('triage_assign_hint')}</p>

      {error && <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>{error}</div>}

      <div className='mb-2'>
        {ComboBox ? (
          <ComboBox
            label={t('triage_assign_client_label')}
            placeholder={t('triage_assign_client_placeholder')}
            noResultText={t('triage_assign_no_clients')}
            value={selectedClient}
            onChange={(option) => setSelectedClient(option || null)}
            onInputChange={(text) => setClientSearchText(text)}
            options={clientOptions}
            isLoading={loadingClients}
            isClearable
          />
        ) : (
          <>
            <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
              {t('triage_assign_client_label')}
            </label>
            <input
              type='text'
              className='form-control form-control-sm mb-1'
              placeholder={t('triage_assign_client_placeholder')}
              value={clientSearchText}
              onChange={(e) => setClientSearchText(e.target.value)}
            />
            {clientOptions.length > 0 && (
              <ul className='list-group mb-2' style={{ fontSize: 13, maxHeight: 200, overflowY: 'auto' }}>
                {clientOptions.map((c) => (
                  <li
                    key={c.value}
                    className='list-group-item list-group-item-action py-1'
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedClient(c)}
                  >
                    {c.label}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {selectedClient && (
        <>
          <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
            {t('triage_assign_case_select_label')}
          </label>
          {casesLoading ? (
            <p className='font-light' style={{ fontSize: 12 }}>{t('triage_sending')}</p>
          ) : eppCases.length === 0 ? (
            <p className='font-light mb-2' style={{ color: '#B45309', fontSize: 13 }}>
              {t('triage_assign_no_eepp_cases')}
            </p>
          ) : (
            <div className='mb-2'>
              {eppCases.map((c) => (
                <div key={c.caseId} className='form-check' style={{ fontSize: 13 }}>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='triage-eepp-case'
                    id={`triage-case-${c.caseId}`}
                    checked={selectedCaseId === c.caseId}
                    onChange={() => setSelectedCaseId(c.caseId)}
                  />
                  <label className='form-check-label' htmlFor={`triage-case-${c.caseId}`}>
                    {c.title || c.code || `EEPP ${c.caseId}`}
                  </label>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
        {t('triage_resolve_notes_label')}
      </label>
      <textarea
        className='form-control form-control-sm mb-3'
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <div className='d-flex justify-content-end gap-2'>
        {Button ? (
          <>
            <Button label={t('triage_btn_cancel')} size='sm' variant='secondary' onClick={onClose} disabled={sending} />
            <Button
              label={sending ? t('triage_sending') : t('triage_btn_confirm')}
              size='sm'
              variant='primary'
              onClick={handleConfirm}
              disabled={sending || !selectedCaseId}
            />
          </>
        ) : (
          <>
            <button type='button' className='btn btn-outline-secondary btn-sm' onClick={onClose} disabled={sending}>
              {t('triage_btn_cancel')}
            </button>
            <button type='button' className='btn btn-primary btn-sm' onClick={handleConfirm} disabled={sending || !selectedCaseId}>
              {sending ? t('triage_sending') : t('triage_btn_confirm')}
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (!Modal) {
    return (
      <div
        role='dialog'
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
        }}
        onClick={onClose}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20, width: 'min(560px, 92vw)' }}>
          <h5>{t('triage_assign_title')}</h5>
          {body}
        </div>
      </div>
    );
  }
  return (
    <Modal title={t('triage_assign_title')} show onClose={onClose} size='md'>
      {body}
    </Modal>
  );
}
